/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Flex,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import moment from "moment-timezone";
import { useEffect, useMemo, useState } from "react";
import {
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { toast } from "react-toastify";
import { usePrompt } from "../../../hooks/usePrompt";
import LoadingSpinner from "../../../LoadingSpinner";
import apiClient from "../../../utils/ApiClient/apiClientAxios";
import { toastService } from "../../../utils/ApiClient/toastService";
import AttachmentsBox from "./AttachmentsBox";
import CaseHistorySection from "./CaseHistorySection";
import CaseLinksBox from "./CaseLinksBox";
import { DataAccordion } from "./DataAccordion";
import { DataGrid } from "./DataGrid";

const CaseTabs = () => {
  const {
    edssData,
    isLoading,
    mutateEdssData,
    fetchEdssCase,
    formState,
    setFormState,
  } = useOutletContext();
  const { id, personID } = useParams();
  const [arrayDataMap, setArrayDataMap] = useState({});

  const [activeTabKey, setActiveTabKey] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const hasUnsavedChanges = useMemo(() => {
    return (
      Object.keys(formState).length > 0 || Object.keys(arrayDataMap).length > 0
    );
  }, [formState, arrayDataMap]);
  usePrompt(
    hasUnsavedChanges,
    "You have unsaved changes. Are you sure you want to leave this page?"
  );
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = ""; // Required for Chrome
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const showUpdateButton = activeTabKey !== "ATTACHMENTS";
  useEffect(() => {
    const params = searchParams.get("tab");
    const keys = Object.keys(edssData || {}).filter(
      (k) => !edssData[k]?.UI_SKIP
    );

    if (keys.length > 0) {
      if (params && keys.includes(params)) {
        setActiveTabKey(params);
        setTabIndex(keys.indexOf(params));
      } else if (!activeTabKey) {
        setActiveTabKey(keys[0]);
        setTabIndex(0);
      }
    }
  }, [edssData, searchParams]);

  if (isLoading) return <LoadingSpinner />;

  const handleArrayFieldChange = (
    tabKey,
    fieldKey,
    index,
    subFieldKey,
    newValue
  ) => {
    const compositeKey = `${tabKey}-${fieldKey}`;
    const originalArray = edssData?.[tabKey]?.FIELDS?.[fieldKey] || [];
    const currentArray = arrayDataMap[compositeKey] ?? originalArray;

    const updatedArray = currentArray.map((item, i) => {
      if (i !== index) return item;
      return {
        ...item,
        [subFieldKey]: {
          ...(item[subFieldKey] || {}),
          VALUE: newValue,
        },
      };
    });
    setArrayDataMap((prev) => ({
      ...prev,
      [compositeKey]: updatedArray,
    }));
  };

  const handleInputChange = (tabKey, fieldKey, value) => {
    if (tabKey !== activeTabKey) return;
    setFormState((prev) => ({
      ...prev,
      [tabKey]: {
        ...(prev[tabKey] || {}),
        [fieldKey]: value,
      },
    }));
  };

  const formatDate = (dateString) => {
    return moment(dateString).format("YYYYMMDD");
  };

  const handleUpdateClick = async () => {
    setIsUpdating(true);
    const payload = {
      id,
      PERSON_ID: personID,
    };

    // Step 1: Scalar field changes
    for (const [tabKey, tabFields] of Object.entries(formState)) {
      const originalFields = edssData?.[tabKey]?.FIELDS || {};
      const changedScalarFields = {};

      for (const [fieldKey, newValue] of Object.entries(tabFields)) {
        const originalValue = originalFields?.[fieldKey]?.VALUE;
        if (newValue !== originalValue) {
          // Check if the field is a date and format it using Moment.js
          if (fieldKey.includes("DATE") && newValue) {
            changedScalarFields[fieldKey] = formatDate(newValue);
          } else {
            changedScalarFields[fieldKey] = newValue;
          }
        }
      }

      if (Object.keys(changedScalarFields).length > 0) {
        payload[tabKey] = {
          ...(payload[tabKey] || {}),
          ...changedScalarFields,
        };
      }
    }

    // Step 2: Array field changes
    for (const [compositeKey, currentArray] of Object.entries(arrayDataMap)) {
      const [tabKey, fieldKey] = compositeKey.split("-");
      const originalArray = edssData?.[tabKey]?.FIELDS?.[fieldKey] || [];

      const flattenItem = (item) =>
        Object.fromEntries(
          Object.entries(item)
            .filter(([key]) => !/preview/i.test(key))
            .map(([key, val]) =>
              typeof val === "object" && val !== null && "VALUE" in val
                ? [key, val.VALUE]
                : [key, val]
            )
        );

      const newArray = currentArray.map((item) => {
        const flattened = flattenItem(item);
        // Format any date fields in the array
        Object.keys(flattened).forEach((key) => {
          if (key.includes("DATE") && flattened[key]) {
            flattened[key] = formatDate(flattened[key]);
          }
        });
        return flattened;
      });

      const originalFlattened = originalArray.map(flattenItem);

      const arraysAreDifferent =
        JSON.stringify(newArray) !== JSON.stringify(originalFlattened);
      if (arraysAreDifferent) {
        if (!payload[tabKey]) payload[tabKey] = {};
        payload[tabKey][fieldKey] = newArray;
      }
    }

    // Step 3: Send if there are any changes
    if (Object.keys(payload).length <= 2) {
      toast.warn("No changes to update.");
      setIsUpdating(false);
      return;
    }
    try {
      const response = await apiClient.post(
        "/saveEDSSCase",
        payload,
        { responseType: "text" } // API returns plain text
      );
      const text = response.data;
      if (text !== "ok") {
        throw new Error(`Update failed: ${text}`);
      }
      toastService.success(response.data);
      fetchEdssCase(id, personID);
      setFormState({});
      setArrayDataMap({});
    } catch (err) {
      console.error("Update error:", err);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteItem = async (tabKey, fieldKey, indexToRemove) => {
    const compositeKey = `${tabKey}-${fieldKey}`;
    const originalArray = edssData?.[tabKey]?.FIELDS?.[fieldKey] || [];
    const currentArray = arrayDataMap[compositeKey] ?? originalArray;

    const updatedArray = currentArray.filter(
      (_, index) => index !== indexToRemove
    );

    setArrayDataMap((prev) => ({
      ...prev,
      [compositeKey]: updatedArray,
    }));
  };

  const handleAddNewItemDirectly = (tabKey, fieldKey, template) => {
    const compositeKey = `${tabKey}-${fieldKey}`;
    const originalArray = edssData?.[tabKey]?.FIELDS?.[fieldKey] || [];
    const currentArray = arrayDataMap[compositeKey] ?? originalArray;
    const newItem = {};
    Object.keys(template || {}).forEach((key) => {
      if (key === "PREVIEW") return;

      newItem[key] = {
        ...(template[key] || {}),
        VALUE: "",
        EDITABLE: true,
      };
    });
    if (template?.PREVIEW) {
      newItem.PREVIEW = {
        ...template.PREVIEW,
        VALUE: "",
        EDITABLE: true,
        PREVIEW_VALUE: template.PREVIEW.PREVIEW_VALUE || [],
      };
    }
    const updatedArray = [newItem, ...currentArray];
    setArrayDataMap((prev) => ({
      ...prev,
      [compositeKey]: updatedArray,
    }));
  };

  return (
    <Box>
      <Tabs
        index={tabIndex}
        variant="enclosed"
        onChange={(index) => {
          const keys = Object.keys(edssData).filter(
            (k) => !edssData[k]?.UI_SKIP
          );
          navigate(`?tab=${keys[index]}`);
          setActiveTabKey(keys[index]);
          setTabIndex(index);
        }}
      >
        <Box
          position="sticky"
          top={-5}
          zIndex="sticky"
          bg="white"
          boxShadow="sm"
        >
          <TabList flexWrap="wrap">
            {Object.keys(edssData).map((tab) => {
              if (!edssData[tab]?.UI_SKIP) {
                return (
                  <Tab fontSize="14px" key={tab}>
                    {edssData[tab].MESSAGE}
                  </Tab>
                );
              }
              return null;
            })}
          </TabList>

          <Flex justify="space-between" align="center" px={4} mt={1}>
            {/* Update Button */}
            {showUpdateButton && (
              <Button
                variant="primary"
                onClick={handleUpdateClick}
                _hover={{ bg: "bgColor", color: "white" }}
                _disabled={{ bg: "gray", color: "black" }}
                isLoading={isUpdating}
                loadingText="Updating...."
              >
                Update
              </Button>
            )}
            {/* Dynamic Add New Button */}
            {(() => {
              const currentSection = edssData?.[activeTabKey];
              if (!currentSection || !currentSection.FIELDS) return null;

              const arrayFields = Object.entries(currentSection.FIELDS).filter(
                ([fieldKey, fieldValue]) =>
                  Array.isArray(fieldValue) &&
                  currentSection.MESSAGE !== "Case Links" &&
                  currentSection.MESSAGE !== "Attachments" &&
                  currentSection.MESSAGE !== "Case History"
              );
              if (arrayFields.length > 0) {
                const [fieldKey, fieldValue] = arrayFields[0];
                return (
                  <Button
                    colorScheme="blue"
                    size="sm"
                    // onClick={() =>
                    //   openAddNewModal(
                    //     activeTabKey,
                    //     fieldKey,
                    //     fieldValue[0] || {}
                    //   )
                    // }
                    onClick={() =>
                      handleAddNewItemDirectly(
                        activeTabKey,
                        fieldKey,
                        fieldValue[0] || {}
                      )
                    }
                  >
                    Add New
                  </Button>
                );
              }
              return null;
            })()}
          </Flex>
        </Box>
        <TabPanels>
          {Object.entries(edssData)
            .filter(([, section]) => !section?.UI_SKIP)
            .map(([tabKey, section]) => (
              <TabPanel key={tabKey}>
                {Object.entries(section.FIELDS).map(
                  ([fieldKey, fieldValue]) => {
                    // ADD THIS
                    if (section?.MESSAGE === "Case History") {
                      console.log(
                        "Rendering field:",
                        fieldKey,
                        "Data:",
                        fieldValue
                      );
                    }
                    const isArray =
                      Array.isArray(fieldValue) &&
                      section?.MESSAGE !== "Case Links" &&
                      section?.MESSAGE !== "Attachments" &&
                      section?.MESSAGE !== "Case History";

                    const compositeKey = `${tabKey}-${fieldKey}`;
                    const currentArray =
                      arrayDataMap[compositeKey] ?? fieldValue;

                    return isArray ? (
                      <Box key={fieldKey} mb={4}>
                        <DataAccordion
                          data={currentArray}
                          tabKey={tabKey}
                          fieldKey={fieldKey}
                          onArrayChange={handleArrayFieldChange}
                          onDeleteItem={(index) =>
                            handleDeleteItem(tabKey, fieldKey, index)
                          }
                        />
                      </Box>
                    ) : section?.MESSAGE === "Case Links" ? (
                      <CaseLinksBox key={fieldKey} data={fieldValue} />
                    ) : section?.MESSAGE === "Attachments" ? (
                      <AttachmentsBox
                        key={fieldKey}
                        caseId={id}
                        personId={personID}
                        attachmentInfo={fieldValue}
                        mutateEdssData={mutateEdssData}
                      />
                    ) : section?.MESSAGE === "Case History" ? (
                      <CaseHistorySection key={fieldKey} data={fieldValue} />
                    ) : null;
                  }
                )}

                {/* Only render DataGrid if there are non-array fields */}
                {section?.MESSAGE !== "Case History" &&
                  section?.MESSAGE !== "Case Links" &&
                  section?.MESSAGE !== "Attachments" && (
                    <DataGrid
                      tabKey={tabKey}
                      data={Object.fromEntries(
                        Object.entries(section.FIELDS).filter(
                          ([, value]) =>
                            !Array.isArray(value) &&
                            typeof value === "object" &&
                            value !== null &&
                            "MESSAGE" in value &&
                            "VALUE" in value
                        )
                      )}
                      formState={formState[tabKey] || {}}
                      onInputChange={(fieldKey, value) =>
                        handleInputChange(tabKey, fieldKey, value)
                      }
                    />
                  )}
              </TabPanel>
            ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default CaseTabs;
